import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, Trophy, Clock, BarChart2, XCircle } from "lucide-react";
import { useAuth } from "../../hooks/AuthenticationProvider";
import { useLocation, useNavigate } from "react-router-dom";
import PvPLobby from "../../components/PvPLobby";
import MultiplayerGame from "../../components/MultiplayerGame";

const StopSignalMultiWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLobby, setShowLobby] = useState(true);
  const [activeMatch, setActiveMatch] = useState(null);

  useEffect(() => {
    // Check if we have a match ID in URL
    const searchParams = new URLSearchParams(location.search);
    const matchId = searchParams.get("matchId");

    if (matchId) {
      setActiveMatch(matchId);
      setShowLobby(false);
    }
  }, [location]);

  const handleMatchCreated = (matchId) => {
    setActiveMatch(matchId);
    setShowLobby(false);
  };

  return (
    <div className="h-full">
      {showLobby ? (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <PvPLobby
            game="stop-signal"
            onMatchCreated={handleMatchCreated}
            onLeaveLobby={() => navigate("/gameroom")}
            gamesData={[{ id: "stop-signal", name: "Stop Signal" }]}
          />
        </div>
      ) : (
        <MultiplayerGame
          matchId={activeMatch}
          gameComponent={StopSignalMulti} // This is your existing component
          onExit={() => navigate("/gameroom")}
        />
      )}
    </div>
  );
};

const StopSignalMulti = ({
  gameState,
  updateGameState,
  submitScore,
  opponent,
}) => {
  const { user } = useAuth();

  // ----------------------------------
  //           GAME STATES
  // ----------------------------------
  const [gameMode, setGameMode] = useState("play"); // "play" | "results"
  const [difficulty, setDifficulty] = useState(1);
  const [gameStarted, setGameStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const timerRef = useRef(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [gameOverHandled, setGameOverHandled] = useState(false);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  // Countdown states
  const [countdown, setCountdown] = useState(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const countdownRef = useRef(null);

  // Stop Signal Task specific states
  const [trials, setTrials] = useState([]);
  const [currentTrialIndex, setCurrentTrialIndex] = useState(0);
  const [currentTrial, setCurrentTrial] = useState(null);
  const [showStimulus, setShowStimulus] = useState(false);
  const [showStopSignal, setShowStopSignal] = useState(false);
  const [stimulusStartTime, setStimulusStartTime] = useState(null);
  const [currentSSD, setCurrentSSD] = useState(200); // Stop Signal Delay (ms)
  const [hasResponded, setHasResponded] = useState(false);

  // Performance metrics
  const [score, setScore] = useState(0);
  const [goTrialCount, setGoTrialCount] = useState(0);
  const [stopTrialCount, setStopTrialCount] = useState(0);
  const [goRTs, setGoRTs] = useState([]);
  const [stopSuccessCount, setStopSuccessCount] = useState(0);
  const [stopFailCount, setStopFailCount] = useState(0);
  const [finalSSD, setFinalSSD] = useState(0);
  const [estimatedSSRT, setEstimatedSSRT] = useState(0);

  // Trial generation and tracking
  const [trialQueue, setTrialQueue] = useState([]);
  const trialTimerRef = useRef(null);
  const stimulusTimerRef = useRef(null);
  const stopSignalTimerRef = useRef(null);

  // ----------------------------------
  //        MULTIPLAYER STATE
  // ----------------------------------
  useEffect(() => {
    console.log("🔍 Game state changed:", {
      status: gameState?.status,
      gameStarted,
      showCountdown,
      isPlaying,
    });

    if (gameState) {
      // If we're waiting for both players to be ready
      if (gameState.status === "pending") {
        console.log("🔄 Game status: pending - resetting state");
        setGameMode("play");
        setGameStarted(false);
        setWaitingForOpponent(false); // Reset waiting state
      }
      // If the game is getting ready to start (both players clicked ready)
      else if (gameState.status === "ready") {
        console.log("🚀 Game status: ready - checking if we should start");
        if (!gameStarted && !showCountdown) {
          console.log("✅ Starting game countdown");
          // Both players are ready, start the countdown
          startGame();
        } else {
          console.log(
            "⏸️ Not starting game - already started or countdown showing",
            {
              gameStarted,
              showCountdown,
            }
          );
        }
      }
      // If the game is active, make sure we're playing
      else if (gameState.status === "active") {
        console.log("🎮 Game status: active - checking if we need to start");
        if (!gameStarted) {
          console.log("🎮 Activating game that wasn't started yet");
          setGameStarted(true);
          // Wait a small tick to ensure state updates
          setTimeout(() => {
            console.log("🎮 Delayed game start to ensure state is updated");
            startGame();
          }, 10);
        } else if (!isPlaying && !showCountdown) {
          console.log(
            "🎮 Game marked as started but not playing yet - starting"
          );
          startGame();
        } else {
          console.log("🎮 Game is already active and running", {
            gameStarted,
            isPlaying,
            showCountdown,
          });
        }
      }
      // If the game is completed, show results
      else if (gameState.status === "completed") {
        console.log("🏁 Game status: completed");
        if (isPlaying) {
          console.log("🏁 Still playing - ending game");
          endGame();
        } else {
          console.log("🏁 Game already ended");
        }
      }

      // Check if opponent is ready (for UI indication)
      if (gameState.playerReady && opponent) {
        const opponentReady = gameState.playerReady[opponent.email] || false;
        console.log(`👥 Opponent ready: ${opponentReady}`);
      }
    }
  }, [gameState, gameStarted, isPlaying, opponent, showCountdown]);

  useEffect(() => {
    if (gameState?.status === "completed" && !gameOverHandled) {
      endGame();
      setGameOverHandled(true);
    }
  }, [gameState?.status, gameOverHandled]);

  // Check if all players are ready
  // Replace the problematic useEffect (around lines 155-166) with this:
  useEffect(() => {
    if (!gameState || !opponent || !user?.email) return;

    // Check if all players are ready
    const playerReadyState = gameState.playerReady || {};
    const allPlayersReady =
      playerReadyState[user.email] && playerReadyState[opponent.email];

    // If all players are ready but game is still pending, transition to "ready" state
    if (allPlayersReady && gameState.status === "pending" && updateGameState) {
      // Use setTimeout to defer the state update until after rendering is complete
      setTimeout(() => {
        updateGameState({
          status: "ready",
        });
      }, 0);
    }
  }, [
    gameState?.playerReady,
    opponent,
    user?.email,
    updateGameState,
    gameState?.status,
  ]);

  // ----------------------------------
  //        GAME & TIMER LOGIC
  // ----------------------------------
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (trialTimerRef.current) clearTimeout(trialTimerRef.current);
      if (stimulusTimerRef.current) clearTimeout(stimulusTimerRef.current);
      if (stopSignalTimerRef.current) clearTimeout(stopSignalTimerRef.current);
    };
  }, [isPlaying]);

  // Start the game with countdown
  const startGame = () => {
    console.log("🔥 startGame CALLED - setting up countdown");
    // Clear any existing countdown timer to prevent multiple running
    if (countdownRef.current) {
      console.log("🔥 Clearing existing countdown timer");
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    setShowCountdown(true);
    setCountdown(3);

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        console.log("🔥 Countdown tick:", prev);
        if (prev <= 1) {
          console.log("🔥 Countdown finished, clearing interval");
          clearInterval(countdownRef.current);
          countdownRef.current = null;

          // Defer this to prevent infinite loops
          setTimeout(() => {
            console.log("🔥 Deferred call to startGameAfterCountdown");
            startGameAfterCountdown();
          }, 0);

          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Add this new helper function that doesn't depend on the isPlaying closure
  const startNextTrialWithCheck = (index, forcePlaying = true) => {
    console.log(
      "⏱️ startNextTrialWithCheck called with index:",
      index,
      "forcePlaying:",
      forcePlaying
    );

    // Safety check - don't proceed if the game is supposed to be over
    if (!forcePlaying) {
      console.log("⏱️ Not playing anymore, ignoring startNextTrial call");
      return;
    }

    // Check if the index is valid
    if (index >= trials.length) {
      console.log("⏱️ Trial index out of bounds, ending game");
      endGame();
      return;
    }

    // Clear any existing timers first
    if (trialTimerRef.current) {
      clearTimeout(trialTimerRef.current);
      trialTimerRef.current = null;
    }
    if (stimulusTimerRef.current) {
      clearTimeout(stimulusTimerRef.current);
      stimulusTimerRef.current = null;
    }
    if (stopSignalTimerRef.current) {
      clearTimeout(stopSignalTimerRef.current);
      stopSignalTimerRef.current = null;
    }

    // Get the trial data - IMPORTANT: Store in local variable
    const trial = trials[index];
    if (!trial) {
      console.log("⏱️ No trial data found for index:", index);
      return;
    }

    console.log("⏱️ Starting trial:", trial);

    // Set trial state
    setCurrentTrial(trial);
    setCurrentTrialIndex(index);
    setHasResponded(false);
    setShowStimulus(false);
    setShowStopSignal(false);

    // Show fixation cross for 500ms, then show stimulus
    stimulusTimerRef.current = setTimeout(() => {
      console.log("⏱️ STIMULUS TIMER FIRED - setting showStimulus to TRUE");
      setShowStimulus(true);
      setStimulusStartTime(Date.now());

      // For stop trials, show stop signal after SSD
      if (trial.isStopTrial) {
        stopSignalTimerRef.current = setTimeout(() => {
          console.log("⏱️ Showing stop signal");
          setShowStopSignal(true);
        }, currentSSD);
      }
    }, 500);

    // Auto-advance to next trial after timeout (if no response)
    trialTimerRef.current = setTimeout(() => {
      console.log("⏱️ Trial timeout reached, hasResponded:", hasResponded);

      // Important: use a function to get the latest state value
      setHasResponded((prevHasResponded) => {
        if (!prevHasResponded) {
          // Missing a response on a go trial is an error
          if (!trial.isStopTrial) {
            console.log("⏱️ Handling missed response");
            handleMissedResponseForTrial(index);
          } else {
            // Successfully stopped on a stop trial
            console.log("⏱️ Handling successful stop");
            handleSuccessfulStopForTrial(index);
          }
        }
        return prevHasResponded;
      });
    }, 2000);
  };

  // Start the actual game after countdown
  const startGameAfterCountdown = () => {
    console.log("🎮 startGameAfterCountdown called");

    setShowCountdown(false);

    // Reset all game states
    setScore(0);
    setGoTrialCount(0);
    setStopTrialCount(0);
    setGoRTs([]);
    setStopSuccessCount(0);
    setStopFailCount(0);
    setCurrentSSD(200); // Reset SSD
    setFinalSSD(0);
    setEstimatedSSRT(0);

    // Generate trials
    const newTrials = generateTrials(getTrialCountForDifficulty(difficulty));
    console.log("Generated trials:", newTrials);

    // Set time limit based on difficulty
    if (difficulty === 1) setTimeLeft(90);
    if (difficulty === 2) setTimeLeft(75);
    if (difficulty === 3) setTimeLeft(60);

    // Safety check for trial data
    if (!newTrials || newTrials.length === 0) {
      console.error("Failed to generate trials!");
      return;
    }

    // Important: Set these states BEFORE setting isPlaying
    setTrials(newTrials);
    setCurrentTrialIndex(0);
    setCurrentTrial(newTrials[0]);
    setHasResponded(false);
    setShowStimulus(false);
    setShowStopSignal(false);
    setGameEnded(false);
    setScoreSubmitted(false);

    // IMPORTANT: Set playing state BEFORE updating game state
    setIsPlaying(true);

    // Update the game state
    if (updateGameState) {
      updateGameState({
        status: "active",
        playerReady: true,
        myProgress: 0,
        myScore: 0,
      });
    }

    // Start the first trial with a delay AFTER setting isPlaying
    setTimeout(() => {
      console.log("🎮 Starting first trial - isPlaying:", true);

      // Show the stimulus for the first trial
      setShowStimulus(true);
      setStimulusStartTime(Date.now());

      // If first trial is a stop trial, schedule the stop signal
      if (newTrials[0].isStopTrial) {
        stopSignalTimerRef.current = setTimeout(() => {
          setShowStopSignal(true);
        }, currentSSD);
      }

      // Schedule auto-advance if no response after timeout
      trialTimerRef.current = setTimeout(() => {
        if (!hasResponded) {
          if (!newTrials[0].isStopTrial) {
            console.log("Auto-advancing first trial - missed response");
            handleMissedResponse();
          } else {
            console.log("Auto-advancing first trial - successful stop");
            handleSuccessfulStop();
          }
        }
      }, 2000);
    }, 500);
  };

  // Generate trials
  const generateTrials = (numTrials) => {
    const trials = [];
    const arrows = ["left", "right"];

    // Determine stop signal probability (e.g., 25%)
    const stopSignalProbability = 0.25;

    for (let i = 0; i < numTrials; i++) {
      const direction = arrows[Math.floor(Math.random() * arrows.length)];
      const isStopTrial = Math.random() < stopSignalProbability;

      trials.push({
        direction,
        isStopTrial,
        trialNumber: i + 1,
      });
    }

    return trials;
  };

  // Decide how many trials
  const getTrialCountForDifficulty = (lvl) => {
    if (lvl === 1) return 40;
    if (lvl === 2) return 60;
    return 80;
  };

  // Start next trial
  const startNextTrial = (index, trialList) => {
    console.log("⏱️ startNextTrial called with index:", index);
    console.log("⏱️ Current trial list:", trialList);

    // Check the current value of isPlaying by logging it
    console.log("⏱️ isPlaying state:", isPlaying);

    // If not playing, ignore the call
    if (!isPlaying) {
      console.log("⏱️ Not playing anymore, ignoring startNextTrial call");
      return;
    }

    // Rest of your function...
    const trial = trialList[index];
    console.log("⏱️ Starting trial:", trial);

    setCurrentTrial(trial);
    setCurrentTrialIndex(index);
    setHasResponded(false);
    setShowStimulus(false);
    setShowStopSignal(false);

    // Show fixation cross for 500ms, then show stimulus
    stimulusTimerRef.current = setTimeout(() => {
      // Capture the current value of isPlaying again to avoid stale closure
      if (!isPlaying) {
        console.log("⏱️ STIMULUS TIMER - game no longer playing, aborting");
        return;
      }

      console.log("⏱️ STIMULUS TIMER FIRED - setting showStimulus to TRUE");
      setShowStimulus(true);
      setStimulusStartTime(Date.now());

      // For stop trials, show stop signal after SSD
      if (trial.isStopTrial) {
        stopSignalTimerRef.current = setTimeout(() => {
          if (!isPlaying) return; // Another closure check
          console.log("⏱️ Showing stop signal");
          setShowStopSignal(true);
        }, currentSSD);
      }
    }, 500);

    // Auto-advance to next trial after timeout (if no response)
    trialTimerRef.current = setTimeout(() => {
      // Capture the current value of isPlaying again
      if (!isPlaying) {
        console.log("⏱️ TIMEOUT TIMER - game no longer playing, aborting");
        return;
      }

      console.log("⏱️ Trial timeout reached, hasResponded:", hasResponded);
      if (!hasResponded) {
        // Missing a response on a go trial is an error
        if (!trial.isStopTrial) {
          console.log("⏱️ Handling missed response");
          handleMissedResponse();
        } else {
          // Successfully stopped on a stop trial
          console.log("⏱️ Handling successful stop");
          handleSuccessfulStop();
        }
      }
    }, 2000); // 2000ms max trial duration
  };

  // Add this useEffect near your other useEffect hooks
  useEffect(() => {
    if (gameEnded && isPlaying) {
      endGame();
    }
  }, [gameEnded, isPlaying]);

  // Handler for the Ready to Play button
  const handleReadyClick = () => {
    console.log("Ready to Play clicked");
    setWaitingForOpponent(true);

    // Update the game state to indicate this player is ready
    if (updateGameState) {
      updateGameState({
        playerReady: {
          ...(gameState?.playerReady || {}),
          [user.email]: true,
        },
        // Add player device/connection info
        playerInfo: {
          ...(gameState?.playerInfo || {}),
          [user.email]: {
            device: navigator.userAgent,
            connectionTime: new Date().toISOString(),
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
          },
        },
      });
    }
  };

  // Handle key/button press responses
  const handleResponse = (direction) => {
    if (hasResponded || !isPlaying || !showStimulus) return;

    setHasResponded(true);

    // Calculate reaction time
    const responseTime = Date.now() - stimulusStartTime;

    if (currentTrial?.isStopTrial) {
      // Failed to stop on a stop trial
      handleFailedStop(responseTime);
    } else {
      // Response on a go trial
      handleGoResponse(direction, responseTime);
    }

    // Show next trial after a brief delay
    setTimeout(() => {
      const nextIndex = currentTrialIndex + 1;
      if (nextIndex >= trials.length) {
        endGame();
        return;
      }

      // Set up next trial
      const nextTrial = trials[nextIndex];
      setCurrentTrialIndex(nextIndex);
      setCurrentTrial(nextTrial);
      setHasResponded(false);
      setShowStimulus(false);
      setShowStopSignal(false);

      // Show the stimulus after a delay
      setTimeout(() => {
        setShowStimulus(true);
        setStimulusStartTime(Date.now());

        // If it's a stop trial, schedule stop signal
        if (nextTrial.isStopTrial) {
          setTimeout(() => {
            setShowStopSignal(true);
          }, currentSSD);
        }

        // Auto-advance if no response after timeout
        setTimeout(() => {
          if (!hasResponded) {
            if (!nextTrial.isStopTrial) {
              handleMissedResponse();
            } else {
              handleSuccessfulStop();
            }
          }
        }, 2000);
      }, 500);
    }, 400);
  };

  // Handle successful response on a go trial
  const handleGoResponse = (direction, rt) => {
    const isCorrect = direction === currentTrial.direction;

    setGoTrialCount((prev) => prev + 1);
    setGoRTs((prev) => [...prev, rt]);

    if (isCorrect) {
      // Add points for correct go response
      const points = Math.max(10, 30 - Math.floor(rt / 50)); // Faster responses get more points
      setScore((prev) => prev + points);

      // Update game state
      if (updateGameState) {
        updateGameState({
          myProgress: (currentTrialIndex + 1) / trials.length,
          myScore: score + points,
          myLastResponse: {
            correct: true,
            points,
            isGo: true,
            rt,
          },
        });
      }
    } else {
      // Penalty for incorrect go response
      setScore((prev) => Math.max(0, prev - 5));

      // Update game state
      if (updateGameState) {
        updateGameState({
          myProgress: (currentTrialIndex + 1) / trials.length,
          myScore: Math.max(0, score - 5),
          myLastResponse: {
            correct: false,
            points: -5,
            isGo: true,
            rt,
          },
        });
      }
    }
  };

  // Handle successful stop on a stop trial
  const handleSuccessfulStop = () => {
    setStopTrialCount((prev) => prev + 1);
    setStopSuccessCount((prev) => prev + 1);

    // Add points for successful stopping
    const points = 15;
    setScore((prev) => prev + points);

    // Adjust SSD upward (making next stop trial harder)
    setCurrentSSD((prev) => Math.min(prev + 50, 500));

    // Update game state
    if (updateGameState) {
      updateGameState({
        myProgress: (currentTrialIndex + 1) / trials.length,
        myScore: score + points,
        myLastResponse: {
          correct: true,
          points,
          isGo: false,
          successfulStop: true,
        },
      });
    }
  };

  // Handle failure to stop on stop trial
  const handleFailedStop = (rt) => {
    setStopTrialCount((prev) => prev + 1);
    setStopFailCount((prev) => prev + 1);

    // Small penalty for failing to stop
    setScore((prev) => Math.max(0, prev - 2));

    // Adjust SSD downward (making next stop trial easier)
    setCurrentSSD((prev) => Math.max(prev - 50, 50));

    // Update game state
    if (updateGameState) {
      updateGameState({
        myProgress: (currentTrialIndex + 1) / trials.length,
        myScore: Math.max(0, score - 2),
        myLastResponse: {
          correct: false,
          points: -2,
          isGo: false,
          successfulStop: false,
          rt,
        },
      });
    }
  };

  // Handle missed response on go trial
  const handleMissedResponse = () => {
    setGoTrialCount((prev) => prev + 1);

    // Penalty for missing a go response
    setScore((prev) => Math.max(0, prev - 5));

    // Update game state
    if (updateGameState) {
      const nextScore = Math.max(0, score - 5);
      const nextProgress = (currentTrialIndex + 1) / trials.length;

      updateGameState({
        myProgress: nextProgress,
        myScore: nextScore,
        myLastResponse: {
          correct: false,
          points: -5,
          isGo: true,
          missed: true,
        },
      });
    }

    // Calculate the next index locally to avoid closure issues
    const nextIndex = currentTrialIndex + 1;

    // Move to next trial
    setTimeout(() => {
      // Don't use state in the closure - use the local variable
      startNextTrialWithCheck(nextIndex, isPlaying);
    }, 500);
  };

  // End the game
  const endGame = () => {
    console.log("🏁 endGame called - Game reasons:", {
      scoreSubmitted,
      gameEnded,
      isPlaying,
      currentTrialIndex,
      trialsLength: trials.length,
    });

    // Add check to prevent premature game ending
    if (isPlaying && currentTrialIndex < 5 && !gameEnded) {
      console.log("🚨 WARNING: Game tried to end too early, ignoring");
      return;
    }

    if (scoreSubmitted) {
      console.log("🏁 Score already submitted, ignoring endGame call");
      return;
    }

    setScoreSubmitted(true);

    // Clean up timers
    if (timerRef.current) clearInterval(timerRef.current);
    if (trialTimerRef.current) clearTimeout(trialTimerRef.current);
    if (stimulusTimerRef.current) clearTimeout(stimulusTimerRef.current);
    if (stopSignalTimerRef.current) clearTimeout(stopSignalTimerRef.current);

    console.log("🏁 Setting isPlaying to FALSE and changing to results mode");
    setIsPlaying(false);
    setGameMode("results");

    // Calculate final metrics
    const avgGoRT =
      goRTs.length > 0
        ? goRTs.reduce((sum, rt) => sum + rt, 0) / goRTs.length
        : 0;

    // Calculate SSRT using the integration method
    // SSRT = nth percentile go RT - SSD
    const calculatedSSRT = calculateSSRT(
      goRTs,
      currentSSD,
      stopSuccessCount,
      stopTrialCount
    );

    setFinalSSD(currentSSD);
    setEstimatedSSRT(calculatedSSRT);

    // Submit the score + detailed stats
    if (submitScore) {
      // Create a stats object with all the performance metrics
      const stats = {
        goTrialCount,
        stopTrialCount,
        stopSuccessCount,
        stopFailCount,
        finalSSD: currentSSD,
        estimatedSSRT: calculatedSSRT,
        averageGoRT: avgGoRT,
        stopSuccessRate:
          stopTrialCount > 0 ? stopSuccessCount / stopTrialCount : 0,
        gameLength: 60 - timeLeft,
        timestamp: new Date(),
      };

      // Submit both score and stats
      submitScore(score, stats);

      // Also update the game state one last time with final score and progress
      if (updateGameState) {
        updateGameState({
          myProgress: 1, // 100% progress
          myScore: score,
          myLastResponse: {
            gameComplete: true,
            finalStats: stats,
          },
        });
      }
    }
  };

  // Calculate SSRT using the integration method
  const calculateSSRT = (goRTs, ssd, successCount, totalStopTrials) => {
    if (goRTs.length === 0 || totalStopTrials === 0) return 0;

    // Sort the go RTs
    const sortedGoRTs = [...goRTs].sort((a, b) => a - b);

    // Calculate the stop failure probability
    const pStopFailure = (totalStopTrials - successCount) / totalStopTrials;

    // Find the nth percentile go RT
    const index = Math.floor(pStopFailure * sortedGoRTs.length);
    const nthGoRT = sortedGoRTs[index] || sortedGoRTs[sortedGoRTs.length - 1];

    // SSRT = nth percentile go RT - SSD
    return Math.max(0, nthGoRT - ssd);
  };

  // Format time
  const formatTime = (sec) => {
    const mins = Math.floor(sec / 60);
    const secs = sec % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  useEffect(() => {
    // Check if the opponent has quit
    if (gameState?.playerQuit && gameState.playerQuit !== user?.email) {
      // If we're still playing, end the game automatically with a win
      if (isPlaying) {
        endGame();
        // You could optionally show a special message:
        alert("Your opponent has left the game. You win by forfeit!");
      }
    }
  }, [gameState?.playerQuit, user?.email, isPlaying]);

  // ----------------------------------
  //            RENDER
  // ----------------------------------
  return (
    <div className="w-full text-white p-0">
      <div className="w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-emerald-300 font-medium flex items-center gap-2">
            <Brain size={18} className="text-emerald-300" />
            <span>Stop Signal Challenge</span>
          </div>
        </div>

        {/* Game Container */}
        <div className="bg-slate-800 rounded-xl overflow-hidden">
          {/* Play Screen */}
          {gameMode === "play" && (
            <div className="relative p-6 md:p-8">
              {/* Start button or waiting indicator */}
              {!gameStarted && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  {waitingForOpponent ? (
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 border-4 border-t-transparent border-emerald-500 rounded-full animate-spin"></div>
                      <p className="text-white text-xl">
                        Waiting for opponent...
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={handleReadyClick}
                      className="py-3 px-6 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-lg"
                    >
                      Ready to Play
                    </button>
                  )}
                </div>
              )}

              {/* Countdown overlay */}
              {showCountdown && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  <div className="flex flex-col items-center">
                    <div className="text-6xl font-bold text-white mb-4">
                      {countdown > 0 ? countdown : "GO!"}
                    </div>
                    <div className="text-xl text-slate-300">Get ready...</div>
                  </div>
                </div>
              )}

              {/* Game stats header */}
              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-purple-500/20 to-indigo-600/20 px-4 py-2 rounded-lg">
                  <span className="text-purple-200 font-medium">
                    Difficulty: {difficulty}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>

                  <div className="ml-4 bg-gradient-to-r from-purple-500/20 to-indigo-600/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              {/* Opponent progress */}
              {gameState && opponent && (
                <div className="mb-4 bg-slate-800/60 p-3 rounded-lg">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-slate-300">
                      {opponent.name}'s Progress
                    </span>
                    <span className="text-xs text-white">
                      {gameState?.progress && gameState.progress[opponent.email]
                        ? `${Math.round(
                            gameState.progress[opponent.email] * 100
                          )}%`
                        : "0%"}
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="bg-purple-500 h-full rounded-full"
                      style={{
                        width: `${
                          ((gameState?.progress &&
                            gameState.progress[opponent.email]) ||
                            0) * 100
                        }%`,
                      }}
                    ></div>
                  </div>
                  {gameState?.scores &&
                    gameState.scores[opponent.email] !== undefined && (
                      <div className="mt-1 text-right text-xs text-purple-300">
                        Score: {gameState.scores[opponent.email]}
                      </div>
                    )}
                </div>
              )}

              {/* Game content */}
              <div className="flex flex-col items-center justify-center">
                {/* Trial progress */}
                <div className="w-full mb-8">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-400 text-sm">Trial</span>
                    <span className="text-white font-medium">
                      {currentTrialIndex + 1}/{trials.length}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 to-indigo-600"
                      style={{
                        width: `${(currentTrialIndex / trials.length) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>

                {/* Stimulus display */}
                <div className="relative flex flex-col items-center justify-center w-64 h-64 bg-slate-900/60 border border-slate-700 rounded-xl mb-6">
                  <AnimatePresence mode="wait">
                    {!showStimulus && (
                      <motion.div
                        key="fixation"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="text-4xl font-bold text-white"
                      >
                        +
                      </motion.div>
                    )}

                    {showStimulus && !showStopSignal && (
                      <motion.div
                        key={`arrow-${currentTrial?.direction}-${currentTrialIndex}`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2 }}
                        className="text-6xl font-bold text-white"
                      >
                        {currentTrial?.direction === "left" ? "←" : "→"}
                      </motion.div>
                    )}

                    {showStopSignal && (
                      <motion.div
                        key={`stop-signal-${currentTrialIndex}`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1.2 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.1 }}
                        className="absolute text-6xl font-bold text-red-500"
                      >
                        ⬤
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Response buttons */}
                <div className="flex gap-4 justify-center mb-8">
                  <button
                    onClick={() => handleResponse("left")}
                    disabled={!showStimulus || hasResponded || showStopSignal}
                    className={`py-3 px-10 text-2xl font-bold rounded-lg transition-colors ${
                      !showStimulus || hasResponded || showStopSignal
                        ? "bg-slate-700 text-slate-500 cursor-not-allowed"
                        : "bg-slate-700 hover:bg-slate-600 text-white"
                    }`}
                  >
                    ←
                  </button>

                  <button
                    onClick={() => handleResponse("right")}
                    disabled={!showStimulus || hasResponded || showStopSignal}
                    className={`py-3 px-10 text-2xl font-bold rounded-lg transition-colors ${
                      !showStimulus || hasResponded || showStopSignal
                        ? "bg-slate-700 text-slate-500 cursor-not-allowed"
                        : "bg-slate-700 hover:bg-slate-600 text-white"
                    }`}
                  >
                    →
                  </button>
                </div>

                {/* Instructions reminder */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-300 max-w-md text-center">
                  Press the arrow button that matches the arrow on screen, but{" "}
                  <span className="text-red-400 font-medium">STOP</span> your
                  response if you see the red circle.
                </div>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You scored {score} points in your multiplayer match!
                </p>
              </div>

              {/* Match results comparison */}
              {gameState && gameState.status === "completed" && opponent && (
                <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-4 mb-6">
                  <h3 className="text-lg font-bold text-white text-center mb-4">
                    Match Results
                  </h3>

                  <div className="flex justify-between items-center mb-6">
                    <div className="text-center flex-1 p-3">
                      <div className="text-lg font-medium text-white">
                        {user?.firstName || user?.email?.split("@")[0]}
                      </div>
                      <div className="text-3xl font-bold text-purple-300 mt-2">
                        {score}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">points</div>
                    </div>

                    <div className="flex flex-col items-center px-4">
                      <div className="text-xl font-bold text-white">VS</div>
                      <div className="w-px h-16 bg-slate-600 my-2"></div>
                    </div>

                    <div className="text-center flex-1 p-3">
                      <div className="text-lg font-medium text-white">
                        {opponent.name}
                      </div>
                      <div className="text-3xl font-bold text-purple-300 mt-2">
                        {gameState.scores?.[opponent.email] || 0}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">points</div>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    {score > (gameState.scores?.[opponent.email] || 0) ? (
                      <div className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 py-2 px-4 rounded-lg font-medium">
                        You Won! 🏆
                      </div>
                    ) : score === (gameState.scores?.[opponent.email] || 0) ? (
                      <div className="bg-amber-500/20 border border-amber-500/30 text-amber-300 py-2 px-4 rounded-lg font-medium">
                        It's a Tie! 🤝
                      </div>
                    ) : (
                      <div className="bg-blue-500/20 border border-blue-500/30 text-blue-300 py-2 px-4 rounded-lg font-medium">
                        {opponent.name} Won! Better luck next time!
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Explanation box */}
              <div className="mt-2 p-3 bg-slate-800/60 rounded-lg max-w-xl mx-auto mb-2">
                <p className="text-sm text-slate-300">
                  <span className="font-medium text-purple-300">
                    Stop Signal Reaction Time (SSRT)
                  </span>
                  : Measures how quickly you can cancel an already initiated
                  action.
                  <br />
                  <span className="font-medium text-purple-300">
                    Stop Signal Delay (SSD)
                  </span>
                  : The time between the arrow and stop signal. Higher means
                  better inhibitory control.
                  <br />
                  <span className="font-medium text-purple-300">
                    Lower SSRT
                  </span>
                  : Indicates stronger impulse control. Elite performers often
                  have SSRT under 200ms.
                </p>
              </div>

              {/* Performance stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-purple-300" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-purple-300">
                        {score}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium">
                        {difficulty}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Go Trials</span>
                      <span className="text-white">{goTrialCount}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Stop Trials</span>
                      <span className="text-white">{stopTrialCount}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Successful Stops</span>
                      <span className="text-emerald-300">
                        {stopSuccessCount}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Failed Stops</span>
                      <span className="text-red-300">{stopFailCount}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-purple-300" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    {/* Reaction times */}
                    <div>
                      <p className="text-sm text-slate-300 mb-1">
                        Reaction Time & Control Metrics
                      </p>
                      <div className="flex justify-between text-sm text-slate-300">
                        <span>Average Go RT</span>
                        <span>
                          {goRTs.length > 0
                            ? `${Math.round(
                                goRTs.reduce((a, b) => a + b, 0) / goRTs.length
                              )} ms`
                            : "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="flex justify-between text-sm text-slate-300">
                      <span>Final Stop Signal Delay</span>
                      <span>{finalSSD} ms</span>
                    </div>

                    <div className="flex justify-between text-sm text-slate-300 mb-2">
                      <span>Stopping Success Rate</span>
                      <span>
                        {stopTrialCount > 0
                          ? `${Math.round(
                              (stopSuccessCount / stopTrialCount) * 100
                            )}%`
                          : "N/A"}
                      </span>
                    </div>

                    <div className="border-t border-slate-700 pt-2 text-sm text-slate-300 flex justify-between">
                      <span>Stop Signal Reaction Time</span>
                      <span
                        className={`${
                          estimatedSSRT < 200
                            ? "text-emerald-300"
                            : estimatedSSRT < 300
                            ? "text-amber-300"
                            : "text-red-300"
                        }`}
                      >
                        {estimatedSSRT > 0
                          ? `${Math.round(estimatedSSRT)} ms`
                          : "N/A"}
                      </span>
                    </div>

                    <div className="mt-4">
                      <p className="text-slate-300 text-sm leading-relaxed">
                        A lower SSRT means stronger inhibitory control. Elite
                        performers often have SSRT under 200ms. Keep practicing
                        to improve!
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Return to lobby button */}
              <div className="flex justify-center">
                <button
                  onClick={() => submitScore(score)}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Return to Lobby
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StopSignalMultiWrapper;
